import {
  CSSProperties,
  forwardRef,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useLatestRef } from "../hooks/useLatestRef";

interface StickyScrollProps {
  children: ReactNode;
  scrollRef: RefObject<HTMLElement | null>;
  offsetTop?: number;
  zIndex?: number;
  className?: string;
  stickyClassName?: string;
  onStickyChange?: (isSticky: boolean) => void;
  /**
   * When true, the placeholder height is locked to the content's height
   * measured just before entering sticky mode. This prevents layout jumps
   * when the content shrinks while sticky (e.g. a collapsed swimlane).
   */
  preserveHeight?: boolean;
}

export const StickyScroll = forwardRef<HTMLDivElement, StickyScrollProps>(
  (
    {
      children,
      scrollRef,
      offsetTop = 0,
      zIndex = 100,
      className = "",
      stickyClassName = "is-sticky",
      onStickyChange,
      preserveHeight = false,
    },
    forwardedRef
  ) => {
    const contentRef = useRef<HTMLDivElement>(null);
    // Fan the rendered element out to both the internal contentRef (used for
    // sticky-state detection) and any ref forwarded by the caller.
    const setRef = useCallback(
      (el: HTMLDivElement | null) => {
        contentRef.current = el;
        if (typeof forwardedRef === "function") {
          forwardedRef(el);
        } else if (forwardedRef) {
          (forwardedRef as { current: HTMLDivElement | null }).current = el;
        }
      },
      [forwardedRef]
    );
    const [isSticky, setIsSticky] = useState(false);
    // Mirror of `isSticky` for synchronous reads inside scroll/resize handlers
    // (avoids stale closures and lets `checkSticky` bail out without a
    // functional updater — side effects in updaters run during render and
    // trigger cross-component setState warnings).
    const isStickyRef = useRef(false);
    // Height captured just before entering sticky mode, used with preserveHeight
    // to keep the content area from collapsing when the sticky content shrinks.
    const [preStickHeight, setPreStickHeight] = useState(0);

    // Stable ref for the callback to avoid re-running the effect on identity changes.
    const onStickyChangeRef = useLatestRef(onStickyChange);

    // Detect sticky state by comparing the element's position to the scroll
    // container on each scroll event. When the element is "stuck," its top
    // edge aligns with the container's top edge + offsetTop (within 1px).
    // This avoids a sentinel element that would break grid/flex layouts.
    // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
    useEffect(() => {
      const content = contentRef.current;
      const scrollContainer = scrollRef.current;
      if (!content || !scrollContainer) return;

      const checkSticky = () => {
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const contentTop = content.getBoundingClientRect().top;
        // The element is sticky when it's at (or within 1px of) its sticky offset
        const nowSticky = contentTop <= containerTop + offsetTop + 1;

        if (isStickyRef.current === nowSticky) return;
        isStickyRef.current = nowSticky;

        // Capture height before entering sticky mode
        if (nowSticky && preserveHeight) {
          setPreStickHeight(content.getBoundingClientRect().height);
        }

        setIsSticky(nowSticky);
        onStickyChangeRef.current?.(nowSticky);
      };

      // Check immediately in case we're already scrolled past the threshold
      checkSticky();

      scrollContainer.addEventListener("scroll", checkSticky, {
        passive: true,
      });
      return () => scrollContainer.removeEventListener("scroll", checkSticky);
    }, [scrollRef, offsetTop, preserveHeight, onStickyChangeRef]);

    // Track the content's natural height while sticky so that intentional
    // resizes (e.g. the user collapsing the swimlane) update the preserved
    // height instead of leaving a whitespace gap.
    const childMeasureRef = useRef<HTMLDivElement>(null);

    // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
    useEffect(() => {
      if (!preserveHeight) return;
      const el = childMeasureRef.current;
      if (!el) return;

      const observer = new ResizeObserver(() => {
        if (!isStickyRef.current) return;
        const h = el.getBoundingClientRect().height;
        setPreStickHeight(h);
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, [preserveHeight]);

    const stickyStyle: CSSProperties = {
      position: "sticky",
      top: offsetTop,
      zIndex,
      // When preserveHeight is active and we're sticky, set a minimum height
      // so the sticky area doesn't shrink when the content collapses.
      minHeight: isSticky && preserveHeight ? preStickHeight : undefined,
      // The ResizeObserver above tracks the child's animated height into
      // minHeight; without this, Chrome's scroll anchoring adjusts scrollTop
      // by that delta on every frame and feeds back into scroll-direction
      // detection (header collapse/expand loop).
      overflowAnchor: preserveHeight ? "none" : undefined,
    };

    const contentClassName =
      isSticky && stickyClassName
        ? `${className} ${stickyClassName}`.trim()
        : className;

    return (
      <div ref={setRef} className={contentClassName} style={stickyStyle}>
        {preserveHeight ? (
          <div ref={childMeasureRef}>{children}</div>
        ) : (
          children
        )}
      </div>
    );
  }
);

StickyScroll.displayName = "StickyScroll";
